import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'query-string';
import OptimalMdSupplierBuilder from 'src/builders/OptimalMdSupplierBuilder';
import { BUOY_GPT_API_V1 } from 'src/interviews/requests/settings';

import type { UseQueryResult } from '@tanstack/react-query';
import type {
  IDiagnosis,
  IInterviewResults,
  MiniInterviewType,
  TreatmentSupplier,
} from 'src/packages/polaris/types';

type UseTreatmentSupplierProps = {
  result?: IInterviewResults;
  interviewType: MiniInterviewType;
  query?: string;
  severity?: string;
  isCondensedVariant?: boolean;
};

type RequestOptions = {
  key: string[];
  params: {
    count: number;
    page_types: string[];
    query: string;
  };
}[];

type Response = {
  query: string;
  treatment_suppliers: TreatmentSupplier[];
};

class Requests {
  static DEFAULT_PARAMS = {
    count: 5,
    page_types: ['article', 'blogPost'],
  };

  static create(options: UseTreatmentSupplierProps): RequestOptions {
    const { interviewType, query, result } = options;

    switch (interviewType) {
      case 'dxoc':
        return this.#dxocRequest(result);
      case 'emergency':
        return this.#emergencyRequest(result);
      case 'symptoms':
        return this.#symptomsRequest(result);
      case 'treatment':
        return this.#treatmentRequest(query);
      case 'treatmentGuidance':
        return this.#treatmentGuidanceRequest();
      case 'txoc':
        return this.#txocRequest();
      default:
        throw new Error(`Treatments for interview type: ${interviewType} is not implemented.`);
    }
  }

  static dedupe(suppliers: TreatmentSupplier[]): TreatmentSupplier[] {
    const seen = new Set();
    return suppliers.filter((supplier: TreatmentSupplier) => {
      const duplicate = seen.has(supplier.referring_page_url);
      seen.add(supplier.referring_page_url);
      return !duplicate;
    });
  }

  static deserialize(responses: UseQueryResult<Response>[]) {
    const allTreatmentSuppliers = responses.flatMap((res) => res.data?.treatment_suppliers || []);
    const uniqueTreatmentSuppliers = this.dedupe(allTreatmentSuppliers);

    return {
      data: {
        treatment_suppliers: uniqueTreatmentSuppliers,
      },
      isError: responses.some((res) => res.isError),
      isLoading: responses.some((res) => res.isLoading),
      isSuccess: responses.every((res) => res.isSuccess),
    };
  }

  static #dxocRequest(result?: IInterviewResults) {
    const diagnosis = result?.diagnosis_of_concern;

    if (!diagnosis?.name) {
      return [];
    }

    return [
      {
        key: [`dxoc-treatmentSuppliers-${diagnosis.name}`],
        params: {
          ...this.DEFAULT_PARAMS,
          query: diagnosis.name,
        },
      },
    ];
  }

  static #emergencyRequest(result?: IInterviewResults) {
    const differential = result?.differential || ([] as IDiagnosis[]);

    if (!differential?.length) {
      return [];
    }

    return differential.map((d) => ({
      key: [`emergency-treatmentSuppliers-${d.name}`],
      params: {
        ...this.DEFAULT_PARAMS,
        count: 3,
        query: d.name,
      },
    }));
  }

  static #symptomsRequest(result?: IInterviewResults) {
    const differential = result?.differential || ([] as IDiagnosis[]);

    if (!differential?.length) {
      return [];
    }

    return differential.map((d) => ({
      key: [`symptoms-treatmentSuppliers-${d.name}`],
      params: {
        ...this.DEFAULT_PARAMS,
        count: 3,
        query: d.name,
      },
    }));
  }

  static #treatmentRequest(query?: string) {
    if (!query) {
      return [];
    }

    return [
      {
        key: [`treatment-treatmentSuppliers-${query}`],
        params: {
          ...this.DEFAULT_PARAMS,
          query,
        },
      },
    ];
  }

  static #treatmentGuidanceRequest() {
    // Do not show related treatment module for treatment guidance interviews.
    return [];
  }

  static #txocRequest() {
    // Do not show related treatment module for txoc interviews.
    return [];
  }
}

function useTreatmentSuppliers(props: UseTreatmentSupplierProps) {
  const { result, severity, isCondensedVariant } = props;

  const requests = Requests.create(props);

  const optimalMd = useMemo(() => {
    const supplier = new OptimalMdSupplierBuilder();

    if (result) {
      const differential = result.differential;

      if (differential?.length) {
        supplier.conditions = differential.map((d) => d.name);
        supplier.triageLevel = differential?.[0]?.triage?.level || 0;
        supplier.severity = severity;
        supplier.isCondensedVariant = isCondensedVariant;
      } else {
        supplier.triageLevel = result.triage?.level || 0;
      }
    }

    return supplier;
  }, [isCondensedVariant, result, severity]);

  const queries = requests.map((request) => ({
    queryKey: request.key,
    queryFn: async () => {
      const response = await axios.get(`${BUOY_GPT_API_V1}/search/treatment_suppliers`, {
        params: request.params,
        paramsSerializer: (params) => qs.stringify(params),
      });

      return response.data;
    },
    staleTime: Infinity,
  }));

  const responses = useQueries({
    queries,
  });

  const deserializedResponse = Requests.deserialize(responses);

  return {
    ...deserializedResponse,
    data: {
      ...deserializedResponse.data,
      treatment_suppliers: [
        optimalMd.card, // hard code OMD as the first result
        ...deserializedResponse.data.treatment_suppliers,
      ],
    },
  };
}
export { Requests };
export default useTreatmentSuppliers;
